import { create } from "zustand";

import { AdminAttachments, uploadFileToStorage } from "@/service/api/admin-attachments";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";

export type UploadStatus = "uploading" | "confirming" | "success" | "error";

const CANCELLABLE_STATUSES: UploadStatus[] = ["uploading", "confirming"];

export interface UploadEntry {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  attachmentId?: string;
  error?: string;
  abortController: AbortController;
}

interface UploadStoreState {
  uploads: Record<string, UploadEntry>;
  startUpload: (file: File) => string;
  cancelUpload: (id: string) => void;
  removeUpload: (id: string) => void;
}

const patchUpload = (
  set: (fn: (state: UploadStoreState) => Partial<UploadStoreState>) => void,
  id: string,
  patch: Partial<UploadEntry>,
) => {
  set((state) => {
    const current = state.uploads[id];
    if (!current) return state;
    return { uploads: { ...state.uploads, [id]: { ...current, ...patch } } };
  });
};

const runUpload = async (
  id: string,
  file: File,
  signal: AbortSignal,
  set: (fn: (state: UploadStoreState) => Partial<UploadStoreState>) => void,
) => {
  try {
    const { uploadUrl, attachmentId } = await AdminAttachments.generateUploadUrl({ fileName: file.name, mimeType: file.type }, { signal });
    if (signal.aborted) return;

    await uploadFileToStorage(uploadUrl, file, {
      signal,
      onProgress: (progress) => patchUpload(set, id, { progress }),
    });
    if (signal.aborted) return;

    patchUpload(set, id, { status: "confirming", progress: 100 });

    await AdminAttachments.confirmUpload({ id: attachmentId }, { signal });
    if (signal.aborted) return;

    patchUpload(set, id, { status: "success", attachmentId });
  } catch (error) {
    if (signal.aborted) return;
    patchUpload(set, id, { status: "error", error: getApiErrorMessage(error, "Erro ao enviar arquivo. Tente novamente.") });
  }
};

export const useUploadStore = create<UploadStoreState>()((set, get) => ({
  uploads: {},

  startUpload: (file) => {
    const id = crypto.randomUUID();
    const abortController = new AbortController();

    set((state) => ({
      uploads: { ...state.uploads, [id]: { id, file, status: "uploading", progress: 0, abortController } },
    }));

    void runUpload(id, file, abortController.signal, set);

    return id;
  },

  cancelUpload: (id) => {
    const current = get().uploads[id];
    if (!current || !CANCELLABLE_STATUSES.includes(current.status)) return;

    current.abortController.abort();
    patchUpload(set, id, { status: "error", error: "Upload cancelado." });
  },

  removeUpload: (id) => {
    get().uploads[id]?.abortController.abort();

    set((state) => {
      const next = { ...state.uploads };
      delete next[id];
      return { uploads: next };
    });
  },
}));
